import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// Gives the initial /cart/add.js round trip (triggered by the nested
// product-form-component's native submit) time to finish before the
// fallback sync checks the cart directly. The CartLinesUpdateEvent path
// below is the primary, instant sync mechanism; this is a safety net in
// case that event doesn't reach this element for any reason.
const FALLBACK_SYNC_DELAY_MS = 900;

/**
 * Swaps a Shop page flavour card's "Add" button for a +/- quantity stepper
 * once the card's variant is in the cart, and keeps it in sync with cart
 * state from any source (this stepper, the Add button, the cart drawer,
 * another tab).
 *
 * @extends {Component}
 */
export class SpdCardQuantityComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onStepperChange);
    this.addEventListener('submit', this.#onFormSubmit);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onStepperChange);
    this.removeEventListener('submit', this.#onFormSubmit);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  get #variantId() {
    return this.dataset.variantId;
  }

  /** @param {Event & {detail: {quantity: number}}} event */
  #onStepperChange = (event) => {
    this.#setCartQuantity(event.detail.quantity);
  };

  /**
   * Fallback for the initial "Add" click: the native form submit (fired by
   * the nested product-form-component) bubbles through this element
   * regardless of whether the CartLinesUpdateEvent sync below succeeds. If
   * this element is still showing "0" after the add had time to complete,
   * fetch the cart directly and correct it.
   */
  #onFormSubmit = () => {
    setTimeout(() => {
      if (this.dataset.cartQty !== '0') return;
      this.#refetchCart()
        .then((cart) => this.#syncFromItems(cart.items))
        .catch((error) => console.warn('[spd-card-quantity] Fallback sync failed:', error));
    }, FALLBACK_SYNC_DELAY_MS);
  };

  /** @param {number} quantity */
  async #setCartQuantity(quantity) {
    if (this.dataset.pending === 'true') return;
    this.dataset.pending = 'true';

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: quantity === 0 ? 'remove' : 'update',
        context: 'product',
        lines: [{ merchandiseId: this.#variantId, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: this.#variantId, quantity }),
      }).then((res) => res.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(response),
        detail: {
          items: response.items,
          source: 'spd-card-quantity-component',
          sourceId: this.id,
          itemCount: quantity,
          didError: false,
        },
      });

      this.dataset.cartQty = quantity.toString();
      this.refs.quantitySelector?.setCartQuantity?.(quantity);
    } catch (error) {
      deferred.reject(error);
      console.error('[spd-card-quantity] Failed to update cart:', error);
    } finally {
      delete this.dataset.pending;
    }
  }

  /** @param {Event & {promise?: Promise<any>}} event */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (!detail || detail.sourceId === this.id) return;

        if (detail.items) {
          this.#syncFromItems(detail.items);
        } else {
          this.#refetchCart().then((cart) => this.#syncFromItems(cart.items));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[spd-card-quantity] Cart sync failed:', error);
      });
  };

  /** @param {Array<{variant_id: number, quantity: number}>} items */
  #syncFromItems(items) {
    const match = items.find((item) => item.variant_id?.toString() === this.#variantId);
    const quantity = match ? match.quantity : 0;

    this.dataset.cartQty = quantity.toString();
    this.refs.quantitySelector?.setCartQuantity?.(quantity);
    this.refs.quantitySelector?.setValue?.(quantity.toString());
  }

  async #refetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }
}

if (!customElements.get('spd-card-quantity-component')) {
  customElements.define('spd-card-quantity-component', SpdCardQuantityComponent);
}
